// [목적] 데스크톱 Split Layout 좌측 50% 브랜드 비주얼 패널 — 블루 그라데이션 + 산 실루엣 SVG + 3-zone 콘텐츠
// [이유] 2026-05-16 reauth-redesign — docs/플리오랩/gangwonfolio-web.html AuthShell 좌측 패널 1:1 포팅.
//         이전 warm/earthy 그라데이션 + PNG 마스코트 로고 폐기. 새 디자인:
//         (1) 좌상단(zone 1): 작은 "강" 로고 박스 + "강원캔버스" / "GangwonCanvas" 워드마크.
//         (2) 중앙(zone 2): "대화로 그리는\n강원 여행 폴리오" 헤드라인 + 부카피.
//         (3) 좌하단(zone 3): "한국관광공사 TourAPI 4.0 기반" 푸터.
//         (4) 배경: linear-gradient(135deg, #1A4D8F, #2876D8 60%, #1F6FEB) + SVG 산맥 실루엣 40% 하단.
// [사이드 이팩트]
//   - 본 컴포넌트 단독으로는 라우트에 연결되지 않음. LoginDesktopLayout 에서 mount.
//   - 산 실루엣 SVG 는 inline 2겹(앞쪽 .6 / 뒤쪽 .85 알파)으로 입체감.
//   - aria-hidden="false" — 카피 텍스트는 스크린리더 노출 유지 (이전 presentation 처리 폐기).
//   - imageSrc prop 제공 시 그라데이션 위에 url() 오버레이 — 현 디자인 기본값 미사용.
//   - LogoSlot(size=38, onDark=true) 사용 — 반투명 화이트 박스 + 흰 글자.
// [수정 시 고려사항]
//   - WCAG 2.1 AA: 흰 글자(#fff) vs brandBlueMid(#2876D8) ≈ 4.6:1 (AA 통과).
//                  부카피 rgba(255,255,255,.85) vs gradient mid (#2876D8) ≈ 4:1 (라지 텍스트 AA).
//   - 헤드라인 줄바꿈은 \n 처리 (whiteSpace: 'pre-line').
//   - 패널 폭(50%)은 LoginDesktopLayout 의 grid 가 제어. 본 컴포넌트는 height 100%.
//   - AUTH_COLORS / AUTH_GRADIENTS / AUTH_SPACING / AUTH_BORDER 토큰 경유 — hex/px 직접 입력 금지.

import { Box, Typography } from '@mui/material';

import { LogoSlot } from './AuthChrome.js';
import { AUTH_COLORS, AUTH_GRADIENTS, AUTH_SPACING } from './tokens.js';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface BrandVisualPanelProps {
  /** 배경 이미지 URL (optional). 미제공 시 브랜드 그라데이션 폴백. */
  imageSrc?: string;
  /** 메인 헤드라인 (optional). 기본값: 디자인 원본 카피. */
  headline?: string;
  /** 서브라인 (optional). 기본값: 디자인 원본 카피. */
  subline?: string;
  /** 푸터 라인 (optional). 기본값: TourAPI 라인. */
  footerLine?: string;
}

// ─── 컴포넌트 ────────────────────────────────────────────────────────────────

/**
 * [목적] 데스크톱 Split Layout 좌측 50% 패널 — 3-zone 블루 그라데이션 비주얼
 * [이유] 디자인 원본 1:1 포팅. 모바일에서는 미사용 (LoginMobileLayout 이 단일 컬럼)
 * [사이드 이팩트] 3-zone (top: 로고+워드마크 / mid: 헤드라인 / bottom: TourAPI) flex 분배
 * [수정 시 고려사항] 패널 폭은 LoginDesktopLayout 의 grid 가 제어. 본 컴포넌트는 height 100%
 */
export function BrandVisualPanel({
  imageSrc,
  headline = '대화로 그리는\n강원 여행 폴리오',
  subline = '취향을 자유롭게 말하면 AI가 동선·예산을 풀어 일정으로 만들어줘요.',
  footerLine = '한국관광공사 TourAPI 4.0 기반',
}: BrandVisualPanelProps): JSX.Element {
  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        minHeight: '100vh',
        px: AUTH_SPACING.panelPaddingX,
        py: AUTH_SPACING.panelPaddingY,
        // 배경: 실 이미지 또는 블루 그라데이션 폴백
        background: imageSrc
          ? `url(${imageSrc}) center/cover no-repeat`
          : AUTH_GRADIENTS.brandPanel,
      }}
    >
      {/* 산 실루엣 SVG — 패널 하단 40% 영역 */}
      <Box
        component="svg"
        viewBox="0 0 400 200"
        preserveAspectRatio="none"
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          height: '40%',
          opacity: 0.7,
          pointerEvents: 'none',
        }}
      >
        {/* 앞쪽 산 (밝은 알파) */}
        <path
          d="M0,150 L60,80 L120,120 L180,60 L240,100 L300,40 L360,80 L400,60 L400,200 L0,200 Z"
          fill={AUTH_COLORS.mountainShadow1}
        />
        {/* 뒤쪽 산 (진한 알파) */}
        <path
          d="M0,170 L80,110 L150,140 L230,100 L310,130 L400,90 L400,200 L0,200 Z"
          fill={AUTH_COLORS.mountainShadow2}
        />
      </Box>

      {/* Zone 1 — 좌상단 로고 + 워드마크 */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          color: AUTH_COLORS.surface0,
          display: 'flex',
          alignItems: 'center',
          gap: '11px',
        }}
      >
        <LogoSlot size={38} onDark />
        <Box>
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 800,
              color: AUTH_COLORS.surface0,
              lineHeight: 1.2,
              letterSpacing: '-0.3px',
            }}
          >
            강원캔버스
          </Typography>
          <Typography
            sx={{
              fontSize: 10,
              color: AUTH_COLORS.whiteAlpha70,
              fontFamily: 'JetBrains Mono, ui-monospace, monospace',
              letterSpacing: '0.3px',
              mt: '2px',
            }}
          >
            GangwonCanvas
          </Typography>
        </Box>
      </Box>

      {/* Zone 2 — 중앙 헤드라인 + 서브라인 */}
      <Box sx={{ position: 'relative', zIndex: 2, color: AUTH_COLORS.surface0 }}>
        <Typography
          component="h2"
          sx={{
            fontSize: 34,
            fontWeight: 800,
            color: AUTH_COLORS.surface0,
            lineHeight: 1.2,
            letterSpacing: '-1px',
            whiteSpace: 'pre-line',
            mb: '14px',
          }}
        >
          {headline}
        </Typography>
        <Typography
          sx={{
            fontSize: 14,
            color: AUTH_COLORS.whiteAlpha85,
            lineHeight: 1.65,
            maxWidth: 380,
            letterSpacing: '-0.1px',
          }}
        >
          {subline}
        </Typography>
      </Box>

      {/* Zone 3 — 좌하단 푸터 라인 */}
      <Typography
        sx={{
          position: 'relative',
          zIndex: 2,
          fontSize: 11,
          color: AUTH_COLORS.whiteAlpha60,
          fontFamily: 'JetBrains Mono, ui-monospace, monospace',
          letterSpacing: '0.3px',
        }}
      >
        {footerLine}
      </Typography>
    </Box>
  );
}
